// Display-driven poster enrichment (§11 fast path). Given the films a view is
// about to render, fetch their TMDB posters (one search each, IndexedDB-cached)
// with limited concurrency and return a uri -> posterUrl map. In test mode the
// mock returns no posters, so screenshots stay on the deterministic duotone.

import { useEffect, useState } from "react";
import { enrichFilm } from "./tmdb.ts";

export interface PosterFilm {
  uri: string;
  title: string;
  year?: number;
}

/** Run tasks with a bounded number in flight. */
async function mapLimit<T>(items: T[], limit: number, fn: (item: T) => Promise<void>) {
  let i = 0;
  const workers = Array.from({ length: Math.min(limit, items.length) }, async () => {
    while (i < items.length) {
      const idx = i++;
      await fn(items[idx]);
    }
  });
  await Promise.all(workers);
}

export function usePosters(films: PosterFilm[]): Record<string, string> {
  const [posters, setPosters] = useState<Record<string, string>>({});
  // Stable key so the effect only re-runs when the set of films changes.
  const key = films.map((f) => f.uri).join(",");

  useEffect(() => {
    let cancelled = false;
    const unique = new Map<string, PosterFilm>();
    for (const f of films) if (!unique.has(f.uri)) unique.set(f.uri, f);

    void mapLimit([...unique.values()], 8, async (f) => {
      const meta = await enrichFilm(f.uri, f.title, f.year).catch(() => null);
      if (cancelled || !meta?.posterPath) return;
      setPosters((prev) => (prev[f.uri] ? prev : { ...prev, [f.uri]: meta.posterPath as string }));
    });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return posters;
}
